/**
 * Meal Plans context (supporting): the body of a plan, as a value object (H53).
 *
 * Pure: no I/O. The body is stored as `jsonb`, and **this file is the rule for
 * what may be in it**; the database checks only that it is an object. Every
 * write goes through `parsePlanBody`, so what is stored is always something
 * this file produced.
 *
 * **Light structure, and deliberately no more** (H53 part 4): general guidance,
 * then Days, each holding Meals in one of four slots, each holding free-text
 * Items with an optional quantity. There is no food database and there are no
 * nutrient figures, because those are an external integration and their own
 * ruling. A quantity is text ("1 cup", "150 g", "a handful") for the same
 * reason: a number with a unit field is a nutrition database in disguise.
 *
 * **Lenient about blanks, strict about size.** The editor posts the rows it
 * has, and an empty row somebody added and never filled is not an error worth
 * stopping a save for, so blank items, empty meals and empty days are dropped.
 * Anything too long or too many is refused, because silently truncating what
 * a clinician wrote would change their advice.
 */
import {
  failure,
  type Failure,
  type FieldCode,
} from "@/modules/shared/failure";

export const MEAL_SLOTS = ["breakfast", "lunch", "dinner", "snack"] as const;
export type MealSlot = (typeof MEAL_SLOTS)[number];

/** How each slot reads, on both sides of the plan. */
export const MEAL_SLOT_LABEL: Record<MealSlot, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
};

export const GUIDANCE_MAX = 2000;
export const DAYS_MAX = 14;
export const MEALS_PER_DAY_MAX = 8;
export const ITEMS_PER_MEAL_MAX = 20;
export const DAY_LABEL_MAX = 40;
export const ITEM_MAX = 200;
export const QUANTITY_MAX = 60;

export interface MealItem {
  text: string;
  /** "1 cup", "150 g"; empty when the item needs none. */
  quantity: string;
  /**
   * The clinician's own figures for this item (H54), absent when none were
   * entered. Calories are not here: they are computed (`nutrition.ts`).
   */
  macros?: Macros;
}

/** Grams of each macronutrient for one item (H54). */
export interface Macros {
  protein: number;
  carbs: number;
  fat: number;
}

/** The ceiling on one macro, in grams: a typo guard, not a dietary rule. */
export const MACRO_MAX = 1000;

export interface Meal {
  slot: MealSlot;
  items: MealItem[];
}

export interface PlanDay {
  /** "Monday", "Day 1", "Training days"; never empty once parsed. */
  label: string;
  meals: Meal[];
}

export interface PlanBody {
  guidance: string;
  days: PlanDay[];
}

export const EMPTY_BODY: PlanBody = { guidance: "", days: [] };

export type BodyParse =
  | { ok: true; data: PlanBody }
  | { ok: false; failure: Failure };

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function isSlot(value: unknown): value is MealSlot {
  return (MEAL_SLOTS as readonly unknown[]).includes(value);
}

function refuse(field: string, code: FieldCode): BodyParse {
  return {
    ok: false,
    failure: failure("malformed_input", { fieldErrors: { [field]: code } }),
  };
}

/**
 * Parse a posted body into the value object, or say what is wrong with it.
 *
 * A slot that is not one of the four is a malformed request rather than a
 * field error: the editor only ever offers the four, so anything else was not
 * typed by a person and there is no field to put a sentence under.
 */
export function parsePlanBody(raw: unknown): BodyParse {
  const source = record(raw);

  const guidance = text(source.guidance);
  if (guidance.length > GUIDANCE_MAX) {
    return refuse("guidance", "meal_plan_guidance_too_long");
  }

  const days: PlanDay[] = [];
  for (const rawDay of list(source.days)) {
    const day = record(rawDay);
    const meals: Meal[] = [];

    for (const rawMeal of list(day.meals)) {
      const meal = record(rawMeal);
      if (!isSlot(meal.slot)) {
        return { ok: false, failure: failure("malformed_input") };
      }
      const items: MealItem[] = [];
      for (const rawItem of list(meal.items)) {
        const item = record(rawItem);
        const itemText = text(item.text);
        const quantity = text(item.quantity);
        // A quantity with nothing to measure is a blank row, not an item.
        if (itemText === "") continue;
        if (itemText.length > ITEM_MAX || quantity.length > QUANTITY_MAX) {
          return refuse("days", "meal_plan_entry_too_long");
        }
        const macros = parseMacros(item.macros);
        if (macros === "invalid") return refuse("days", "meal_plan_macro_invalid");
        items.push(macros ? { text: itemText, quantity, macros } : { text: itemText, quantity });
      }
      if (items.length === 0) continue;
      if (items.length > ITEMS_PER_MEAL_MAX) {
        return refuse("days", "meal_plan_items_too_many");
      }
      meals.push({ slot: meal.slot, items });
    }

    if (meals.length === 0) continue;
    if (meals.length > MEALS_PER_DAY_MAX) {
      return refuse("days", "meal_plan_items_too_many");
    }
    const label = text(day.label);
    if (label.length > DAY_LABEL_MAX) {
      return refuse("days", "meal_plan_entry_too_long");
    }
    days.push({ label, meals });
  }

  if (days.length > DAYS_MAX) {
    return refuse("days", "meal_plan_days_too_many");
  }

  // A day somebody left unnamed is called by its place, after the empty ones
  // are gone, so "Day 3" is the third day the reader actually sees.
  return {
    ok: true,
    data: {
      guidance,
      days: days.map((day, index) => ({
        ...day,
        label: day.label || `Day ${index + 1}`,
      })),
    },
  };
}

/**
 * One item's macros, or null when none were entered (H54).
 *
 * **Blank is not zero, until something is entered.** An item with no figures
 * has no macros at all and is counted as "without macros" in the totals; once
 * any one is entered, the blank others count as 0, because "40 g protein, carbs
 * left empty" means none worth counting, not "unknown". Rounded to one decimal:
 * a tenth of a gram is already more precision than a kitchen has.
 */
function parseMacros(raw: unknown): Macros | null | "invalid" {
  const source = record(raw);
  const values: Record<keyof Macros, number | null> = {
    protein: null,
    carbs: null,
    fat: null,
  };
  for (const key of ["protein", "carbs", "fat"] as const) {
    const value = source[key];
    if (value === undefined || value === null || value === "") continue;
    const n = typeof value === "number" ? value : Number(String(value).trim());
    if (!Number.isFinite(n) || n < 0 || n > MACRO_MAX) return "invalid";
    values[key] = Math.round(n * 10) / 10;
  }
  if (values.protein === null && values.carbs === null && values.fat === null) {
    return null;
  }
  return {
    protein: values.protein ?? 0,
    carbs: values.carbs ?? 0,
    fat: values.fat ?? 0,
  };
}

/** How many items the plan holds. A plan with none has nothing to share. */
export function itemCount(body: PlanBody): number {
  return body.days.reduce(
    (total, day) =>
      total + day.meals.reduce((sum, meal) => sum + meal.items.length, 0),
    0,
  );
}

/**
 * A stored body, read back.
 *
 * Everything stored came through `parsePlanBody`, so this should never find
 * anything wrong; when it does (a hand-edited row, a future shape), the plan
 * still opens, empty, rather than taking the screen down.
 */
export function readStoredBody(stored: unknown): PlanBody {
  const parsed = parsePlanBody(stored);
  return parsed.ok ? parsed.data : EMPTY_BODY;
}
